import classNames from 'classnames'
import { Fragment, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useSlate } from 'slate-react'

import { IToolbarButtonView, TOOLBAR_BUTTONS } from '../interface/IToolbar'
import { IToolbarCustomButtons } from '../interface/IToolbarCustomButtons'

import { TOOLBAR_BUTTONS_CONFIGURATION } from './constants'
import { useToolbarButtons } from './hooks/useToolbarButtons'

import styles from './Toolbar.module.scss'

interface IProps {
  disabled: boolean
  toolbarButtonsView?: Partial<IToolbarButtonView>
  toolbarCustomButtons?: IToolbarCustomButtons[]
  fontSizes?: number[]
}

export function Toolbar(props: IProps) {
  const [buttonsView, setButtonsView] = useState<Partial<IToolbarButtonView>>(
    {} as IToolbarButtonView
  )
  const editor = useSlate()

  const Separators = useMemo(() => {
    return <div className={styles.blockSeparator} />
  }, [])

  const { toolbarButtons } = useToolbarButtons(editor, props.disabled, props.fontSizes)

  // This is necessary to maintain focus on the editable when clicking on
  // the text editing buttons, so that the selected text remains visible
  const keepFocusOnEditable = useCallback((e: MouseEvent) => {
    e.preventDefault()
  }, [])

  useEffect(() => {
    const values: Partial<IToolbarButtonView> = props.toolbarButtonsView
      ? { ...props.toolbarButtonsView }
      : Object.values(TOOLBAR_BUTTONS).reduce((acc, item) => {
          acc[item] = true
          return acc
        }, {} as IToolbarButtonView)
    // FontSize is opt-in: only shown when a numeric size grid is provided via the `fontSizes` prop.
    values[TOOLBAR_BUTTONS.FontSize] =
      Boolean(props.fontSizes?.length) && Boolean(values[TOOLBAR_BUTTONS.FontSize])
    setButtonsView(values)
  }, [props.toolbarButtonsView, props.fontSizes])

  const toolbarCustomButtons = useMemo(() => {
    if (!props.toolbarCustomButtons) {
      return null
    }

    return props.toolbarCustomButtons.map((el, index) => (
      <div key={index} className={styles.actionGroup} data-testid={`toolbarCustomButtons-${index}`}>
        <div className={styles.actionGroup__item}>{el.content({ disabled: false, editor })}</div>
      </div>
    ))
  }, [props.toolbarCustomButtons])

  return (
    <div className={styles.root}>
      {Object.keys(TOOLBAR_BUTTONS_CONFIGURATION).map((key) => {
        const currentGroup = TOOLBAR_BUTTONS_CONFIGURATION[key]
        if (currentGroup.buttons.every((button) => !buttonsView[button.key])) {
          return null
        }

        return (
          <Fragment key={key}>
            <div className={styles.actionGroup} data-testid={currentGroup.testId}>
              {currentGroup.buttons.map((button) => {
                return buttonsView[button.key] ? (
                  <div
                    className={classNames(button.className || styles.actionGroup__item)}
                    key={`toolbar-item-${button.key}`}
                    data-testid={button.testId}
                    onMouseDown={button.keepFocusOnClick ? keepFocusOnEditable : undefined}
                  >
                    {toolbarButtons[button.key]()}
                  </div>
                ) : null
              })}
            </div>
            {currentGroup.separatorAfterGroup ? Separators : null}
          </Fragment>
        )
      })}
      {toolbarCustomButtons}
    </div>
  )
}
